import { cn } from '@/lib/utils'
import { Fragment } from 'react'

type Primitive = string | number | boolean

type RulesViewerProps = {
  rules: unknown
  title?: string
  className?: string
  emptyFallback?: string
}

function formatLabel(label: string) {
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, char => char.toUpperCase())
}

function renderPrimitive(value: Primitive) {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return String(value)
}

function renderList(values: unknown[]) {
  if (values.length === 0) return <span className="text-muted-foreground">None</span>
  return (
    <ul className="list-disc pl-5 space-y-1">
      {values.map((item, index) => (
        <li key={index}>{renderValue(item)}</li>
      ))}
    </ul>
  )
}

function renderObject(value: Record<string, unknown>) {
  const entries = Object.entries(value)
  if (entries.length === 0) return <span className="text-muted-foreground">None</span>
  return (
    <dl className="grid grid-cols-1 gap-x-3 gap-y-1 @sm/rules:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      {entries.map(([key, val]) => (
        <Fragment key={key}>
          <dt className="break-words text-sm font-medium text-muted-foreground">
            {formatLabel(key)}
          </dt>
          <dd className="min-w-0 break-words text-sm">{renderValue(val)}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

function renderValue(value: unknown): React.ReactNode {
  if (value == null) {
    return <span className="text-muted-foreground">Not specified</span>
  }

  if (Array.isArray(value)) {
    return renderList(value)
  }

  if (typeof value === 'object') {
    return renderObject(value as Record<string, unknown>)
  }

  return <span>{renderPrimitive(value as Primitive)}</span>
}

const SECTION_ORDER: Array<keyof Record<string, unknown>> = [
  'container',
  'quantity',
  'subcategories',
  'labels',
  'attachments',
  'judgingCriteria',
  'forbidden',
  'notes',
]

export function RulesViewer({
  rules,
  title,
  className,
  emptyFallback = 'No rules available',
}: RulesViewerProps) {
  const record =
    typeof rules === 'object' && rules !== null ? (rules as Record<string, unknown>) : undefined

  if (!record) {
    return <p className={cn('text-sm text-muted-foreground', className)}>{emptyFallback}</p>
  }

  const remaining = { ...record }

  const sections = SECTION_ORDER.filter(key => key in record)

  // Remove known sections so we can render additional ones afterwards
  for (const key of sections) {
    delete remaining[key as string]
  }

  const remainingEntries = Object.entries(remaining)

  return (
    <div className={cn('@container/rules space-y-4', className)}>
      {title && <h3 className="text-base font-semibold tracking-tight">{title}</h3>}
      <div className="space-y-6">
        {sections.map(sectionKey => (
          <div key={sectionKey}>
            <h4 className="text-sm font-semibold text-muted-foreground mb-1">
              {formatLabel(sectionKey as string)}
            </h4>
            <div className="text-sm leading-relaxed">
              {renderValue(record[sectionKey as string])}
            </div>
          </div>
        ))}

        {remainingEntries.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-1">Additional Details</h4>
            <div className="text-sm leading-relaxed">{renderObject(remaining)}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default RulesViewer
